// frontend/src/LandingPage.js
import React, { Suspense, useState } from 'react';
import { ChevronRight, ArrowDown, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginForm from './components/Auth/LoginForm';
import SEO from './components/SEO';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import { getTool, TOOL_GROUPS } from './tools';

const calculatorTool = getTool('savings-calculator');
const CalculatorDemo = calculatorTool.demo;

const KarbonKitLanding = () => {
  const { authenticated } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('signup');

  const handleGetStarted = () => {
    if (authenticated) {
      window.location.href = '/dashboard';
    } else {
      setLoginMode('signup');
      setShowLogin(true);
    }
  };

  const scrollToTools = () => {
    document.getElementById('tools')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-surface-50 font-body">
      <SEO
        title="KarbonKit: Heat pump, solar, and battery website tools"
        description="Embeddable heat pump, solar PV and battery widgets for your website. Help your users explore low-carbon technology options with real UK property data."
        path="/"
      />
      <SiteHeader />

      {/* Login Modal */}
      {showLogin && (
        <LoginForm initialMode={loginMode} onClose={() => setShowLogin(false)} />
      )}

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-6xl text-surface-900 mb-6 leading-[1.1]">
              Give your users the tools to explore low-carbon tech
            </h1>

            <p className="text-lg text-surface-500 mb-10 max-w-2xl leading-relaxed">
              Small tools that answer households&rsquo; questions about heat pumps, solar and batteries, for installers, councils, housing associations and community energy groups to put on their own websites. Paste one line of code – no developer needed. Everything on this page is a working demo, and a free account lets you brand a tool and put it live.
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={scrollToTools}
                className="bg-surface-900 text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-surface-800 transition-colors flex items-center"
              >
                Explore the tools
                <ArrowDown className="ml-1.5 w-4 h-4" />
              </button>
              <button
                onClick={handleGetStarted}
                className="text-sm font-medium text-brand-700 px-6 py-3 rounded-lg border border-brand-200 bg-brand-50 hover:bg-brand-100 transition-colors"
              >
                {authenticated ? 'Go to Dashboard' : 'Get started – free'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tools gallery */}
      <section id="tools" className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-surface-900 mb-4">
              The tools
            </h2>
            <p className="text-surface-500 leading-relaxed">
              Grouped by the question each one helps a household answer.
            </p>
          </div>

          <div className="space-y-12">
            {TOOL_GROUPS.map(({ category, items }, groupIndex) => (
              <div key={category.id}>
                <h3 className="font-display text-xl md:text-2xl text-surface-900 mb-5">
                  {category.name}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <Link
                        key={tool.slug}
                        to={`/tools/${tool.slug}`}
                        className="group bg-surface-50 rounded-xl border border-surface-200 p-6 hover:border-brand-300 hover:shadow-md transition-all flex flex-col"
                      >
                        <div className="w-11 h-11 rounded-lg bg-brand-50 border border-brand-100 text-brand-700 flex items-center justify-center mb-4">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h4 className="font-semibold text-surface-900 text-lg mb-2 group-hover:text-brand-700 transition-colors">
                          {tool.name}
                        </h4>
                        <p className="text-sm text-surface-500 leading-relaxed flex-1">
                          {tool.card}
                        </p>
                        <p className="mt-4 text-sm font-medium text-brand-700 flex items-center">
                          Try the live demo
                          <ChevronRight className="ml-0.5 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </p>
                      </Link>
                    );
                  })}

                  {/* Filler card at the end of the last group: invites suggestions */}
                  {groupIndex === TOOL_GROUPS.length - 1 && (
                    <a
                      href="mailto:hello@karbonkit.com?subject=KarbonKit%20tool%20idea"
                      className="group rounded-xl border border-dashed border-surface-300 p-6 hover:border-brand-300 transition-all flex flex-col justify-center items-start"
                    >
                      <div className="w-11 h-11 rounded-lg bg-surface-100 border border-surface-200 text-surface-400 flex items-center justify-center mb-4 group-hover:text-brand-600 transition-colors">
                        <Plus className="w-5 h-5" />
                      </div>
                      <h4 className="font-semibold text-surface-700 text-lg mb-2">
                        More on the way
                      </h4>
                      <p className="text-sm text-surface-500 leading-relaxed">
                        We build new tools based on what embedders ask for. If something your users need is missing, tell us &rarr;
                      </p>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-surface-900 mb-4">
              How it works
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                step: '1',
                title: 'Create an account',
                desc: 'Sign up for free.',
                img: '/assets/step1.png',
              },
              {
                step: '2',
                title: 'Pick your widget',
                desc: 'Choose whichever of the tools above fits your site.',
                img: '/assets/step2.png',
              },
              {
                step: '3',
                title: 'Personalise it',
                desc: 'Set your brand colours and company name so it looks like part of your site.',
                img: '/assets/step3.jpg',
              },
              {
                step: '4',
                title: 'Paste one line of code',
                desc: 'Copy the embed code from your dashboard and paste it into any web page.',
                img: '/assets/step4.jpg',
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-36 h-36 mx-auto mb-5 rounded-2xl bg-brand-50 border border-brand-100 flex items-center justify-center overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-24 h-24 object-contain" />
                </div>
                <div className="text-xs font-mono text-brand-600 mb-2">Step {item.step}</div>
                <h3 className="font-semibold text-surface-900 mb-2 text-lg">{item.title}</h3>
                <p className="text-surface-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flagship demo: calculator */}
      <section id="calculator-demo" className="py-16 bg-white border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-medium text-brand-600 tracking-wide uppercase mb-3">
              Live demo
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-surface-900 mb-4">
              {calculatorTool.title}
            </h2>
            <p className="text-surface-500 leading-relaxed">
              {calculatorTool.lede}
            </p>
          </div>

          <div className="bg-surface-50 rounded-xl border border-surface-200 overflow-hidden">
            <Suspense fallback={<div className="py-24 text-center text-sm text-surface-400">Loading demo…</div>}>
              <CalculatorDemo />
            </Suspense>
          </div>

          <div className="mt-10 bg-brand-700 rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl text-white mb-2">
                Embed this on your website
              </h3>
              <p className="text-brand-200 max-w-lg">
                {calculatorTool.embedPitch}
              </p>
            </div>
            <button
              onClick={handleGetStarted}
              className="bg-white text-brand-700 px-6 py-3 rounded-lg font-medium text-sm hover:bg-surface-50 transition-colors whitespace-nowrap flex items-center"
            >
              {authenticated ? 'Go to Dashboard' : 'Sign up to customise'}
              <ChevronRight className="ml-1 w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-surface-900 mb-4">
              What's included
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
            {[
              {
                title: 'Interactive tools',
                desc: 'Calculators, visualisers and simulators: type in a postcode, listen to a recording, walk through a 3D house.',
              },
              {
                title: 'Built on public data',
                desc: 'Calculations start from the property’s EPC record and national energy-use figures, adjusted for region and size. Grant details are checked against official sources.',
              },
              {
                title: 'Custom branding',
                desc: 'Set your own colours and company name so the widget sits naturally in your site. Full white-label is available on request.',
              },
              {
                title: 'One line of code',
                desc: 'Paste it into WordPress, Squarespace, Wix or plain HTML, and it resizes itself to fit. Sites that block scripts can use an iframe instead.',
              },
            ].map((feature, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded bg-brand-100 text-brand-700 flex items-center justify-center font-mono text-sm font-medium flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-surface-900 mb-1">{feature.title}</h3>
                  <p className="text-surface-500 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get started */}
      <section id="get-started" className="py-16 bg-white border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8">
            <h2 className="font-display text-3xl md:text-4xl text-surface-900">
              The widgets are free to use
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleGetStarted}
              className="bg-surface-900 text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-surface-800 transition-colors"
            >
              Get started – free
            </button>
            <a
              href="mailto:hello@karbonkit.com?subject=KarbonKit%20custom%20setup"
              className="text-sm text-surface-600 hover:text-surface-900 transition-colors"
            >
              Need white-label or a custom integration? Get in touch &rarr;
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default KarbonKitLanding;
